import {
  WidgetSlot,
  WidgetPointer,
  PluginsHostApiContext,
} from '@wix/editor-platform-host-integration-apis';
import { CompRef, DocumentServicesObject } from '@wix/document-services-types';

import { PlatformContext } from '../../../../../types/platformApi';

import {
  installPluginApp,
  isAppInstalled,
  reportPluginBi,
  serializePlacement,
} from '../../utils/pluginUtils';

import {
  addPluginTry,
  pluginAdded,
  pluginRemoved,
} from '@wix/bi-logger-editor-platform/v2';
import { IExperimentsApiAdapter } from '../../../../utils/experimentsApiAdapter';
import { platformEvents } from '../../../../../platformEvents';
import {
  PluginHostWidgetApi,
  SitePluginsHostIntegrationApi,
} from '../../types';

export type PluginManagerType = 'blocksAppEditor' | 'editor' | 'sdk';

export interface PluginManagerImplementationAPIs {
  hostApiContext: PluginsHostApiContext;
  platformContext: PlatformContext;
  hostApiMethods: SitePluginsHostIntegrationApi;
  documentServicesAPI: DocumentServicesObject;
  experimentsAPI: IExperimentsApiAdapter;
  resolveHostAPIBySlotRef: (
    slotPlaceholderCompRef: CompRef,
  ) => PluginHostWidgetApi;
}

export interface PluginAddParams {
  slotComponentRef: CompRef;
  widgetPointer: WidgetPointer;
}

export interface PluginRemoveParams {
  slotComponentRef: CompRef;
}

export function getAddPluginSteps(
  params: PluginAddParams,
  apis: PluginManagerImplementationAPIs,
  managerType: PluginManagerType,
): {
  getPlacement: () => Promise<WidgetSlot['placement']>;
  biReportAddTry: (placement: WidgetSlot['placement']) => Promise<void>;
  biReportAddSuccess: (placement: WidgetSlot['placement']) => Promise<void>;
  installPluginAppIfNeeded: () => Promise<void>;
  populate: () => Promise<CompRef>;
  postInstallWait: () => Promise<void>;
  notifyHostApp: (placement: WidgetSlot['placement']) => Promise<void>;
} {
  const {
    hostApiMethods,
    hostApiContext,
    platformContext,
    documentServicesAPI,
    experimentsAPI,
    resolveHostAPIBySlotRef,
  } = apis;
  const { slotComponentRef, widgetPointer } = params;
  return {
    async getPlacement() {
      const { getPlacementInBlocksAppEditor, getWidgetSlotPlacement } =
        hostApiMethods;
      let placement: WidgetSlot['placement'] | undefined;
      switch (managerType) {
        case 'blocksAppEditor': {
          placement = getPlacementInBlocksAppEditor(slotComponentRef.id);
          break;
        }
        case 'editor':
        case 'sdk': {
          placement = getWidgetSlotPlacement(slotComponentRef);
          break;
        }
      }

      if (!placement) {
        throw new Error(
          `Plugins Host API: 'installWidgetPlugin' missing placement params for ${slotComponentRef.id}`,
        );
      }
      return placement;
    },
    async biReportAddTry(placement: WidgetSlot['placement']): Promise<void> {
      const userRole = managerType === 'blocksAppEditor' ? 'dev' : 'user';
      reportPluginBi(
        addPluginTry({
          origin: hostApiContext.origin as string,
          target_type: 'widget_plugins',
          role: userRole,
          editorType: hostApiContext.editorType,
          ...platformContext.intentInstallServices.getBIExtras({
            appDefinitionId: widgetPointer.appDefinitionId,
          }),
          slot_id: slotComponentRef.id,
          plugin_id: widgetPointer.widgetId,
          host_app_id: placement.appDefinitionId,
          placement_id: serializePlacement(placement),
          app_def_id_plugin: widgetPointer.appDefinitionId,
        }),
      );
    },
    async biReportAddSuccess(
      placement: WidgetSlot['placement'],
    ): Promise<void> {
      const userRole = managerType === 'blocksAppEditor' ? 'dev' : 'user';
      reportPluginBi(
        pluginAdded({
          origin: hostApiContext.origin as string,
          target_type: 'widget_plugins',
          role: userRole,
          editorType: hostApiContext.editorType,
          ...platformContext.intentInstallServices.getBIExtras({
            appDefinitionId: widgetPointer.appDefinitionId,
          }),
          slot_id: slotComponentRef.id,
          plugin_id: widgetPointer.widgetId,
          host_app_id: placement.appDefinitionId,
          placement_id: serializePlacement(placement),
          app_def_id_plugin: widgetPointer.appDefinitionId,
        }),
      );
    },
    async installPluginAppIfNeeded() {
      const isPluginAppInstalled = isAppInstalled(
        documentServicesAPI,
        widgetPointer,
      );

      if (!isPluginAppInstalled) {
        await installPluginApp(
          documentServicesAPI,
          widgetPointer,
          hostApiContext.origin,
          experimentsAPI,
        );
      }
    },
    async populate() {
      const compRef = await resolveHostAPIBySlotRef(
        slotComponentRef,
      ).populateWidgetPluginToSlot(
        documentServicesAPI,
        slotComponentRef,
        widgetPointer,
      );
      return compRef;
    },
    async postInstallWait() {
      await documentServicesAPI.waitForChangesAppliedAsync();
    },
    async notifyHostApp(placement: WidgetSlot['placement']) {
      const { applicationId } = documentServicesAPI.tpa.app.getDataByAppDefId(
        placement.appDefinitionId!,
      );
      const hostComponentRef = resolveHostAPIBySlotRef(
        slotComponentRef,
      ).getWidgetHostRef(documentServicesAPI, slotComponentRef);

      documentServicesAPI.platform.notifyApplication(
        applicationId!,
        platformEvents.factory.widgetPluginAdded({
          slotComponentRef,
          hostComponentRef,
          placement: {
            appDefinitionId: placement.appDefinitionId!,
            widgetId: placement.widgetId!,
            slotId: placement.slotId!,
          },
          plugin: widgetPointer,
        }),
      );
    },
  };
}

export function getRemovePluginSteps(
  params: PluginRemoveParams,
  apis: PluginManagerImplementationAPIs,
  managerType: PluginManagerType,
): {
  getPlacement: () => WidgetSlot['placement'];
  removeFromSlot: () => void;
  biReportRemoveSuccess: (placement: WidgetSlot['placement']) => void;
  notifyHostApp: (placement: WidgetSlot['placement']) => Promise<void>;
} {
  const {
    hostApiMethods,
    hostApiContext,
    platformContext,
    documentServicesAPI,
    resolveHostAPIBySlotRef,
  } = apis;
  const { slotComponentRef } = params;
  return {
    getPlacement() {
      return hostApiMethods.getWidgetSlotPlacement(slotComponentRef);
    },
    async removeFromSlot() {
      resolveHostAPIBySlotRef(slotComponentRef).uninstallWidgetPlugin(
        documentServicesAPI,
        slotComponentRef,
      );
    },
    async biReportRemoveSuccess(placement: WidgetSlot['placement']) {
      const userRole = managerType === 'blocksAppEditor' ? 'dev' : 'user';
      const widgetPointer = hostApiMethods.getWidgetPointer(slotComponentRef);
      reportPluginBi(
        pluginRemoved({
          origin: hostApiContext.origin as string,
          target_type: 'widget_plugins',
          role: userRole,
          editorType: hostApiContext.editorType,
          ...platformContext.intentInstallServices.getBIExtras({
            appDefinitionId: widgetPointer?.appDefinitionId,
          }),
          slot_id: slotComponentRef.id,
          plugin_id: widgetPointer?.widgetId,
          host_app_id: placement.appDefinitionId,
          placement_id: serializePlacement(placement),
          app_def_id_plugin: widgetPointer?.appDefinitionId,
        }),
      );
    },
    async notifyHostApp(placement: WidgetSlot['placement']) {
      const { applicationId } = documentServicesAPI.tpa.app.getDataByAppDefId(
        placement.appDefinitionId!,
      );
      const hostComponentRef = resolveHostAPIBySlotRef(
        slotComponentRef,
      ).getWidgetHostRef(documentServicesAPI, slotComponentRef);
      const widgetPointer = hostApiMethods.getWidgetPointer(slotComponentRef);

      if (!widgetPointer) {
        throw new Error(
          `Plugins Host API: 'notifyHostApp' is missing 'widgetPointer' for ${slotComponentRef.id}`,
        );
      }

      documentServicesAPI.platform.notifyApplication(
        applicationId!,
        platformEvents.factory.widgetPluginRemoved({
          slotComponentRef,
          hostComponentRef,
          placement: {
            appDefinitionId: placement.appDefinitionId!,
            widgetId: placement.widgetId!,
            slotId: placement.slotId!,
          },
          plugin: widgetPointer,
        }),
      );
    },
  };
}
